import Vue from 'vue';
import Vuex from 'vuex';
import Board from './modules/board';
import Sockets from './modules/socket';
import Interface from './modules/interface';
import router from '../router';
import createPersistedState from 'vuex-persistedstate';
import { RootState, ActionsPayload, MutationsKeys, MutationsPayload, ActionsKeys, GettersKeys } from './types';
import { SocketActionsKeys } from './types/socket';
import './socketPlugin';
import { BoardActionsKeys } from './types/board';
import { SessionActionsKeys } from './types/session';
import API from '@/plugins/axios';
import AzureAuth from '@/plugins/AzureAuth';

Vue.use(Vuex);

const rootState = () => {
  const state: RootState = {
    version: '0.0.1',
    tnc_accepted: false,
    tnc_accepted_at: null,
    currentUser: null,
    jwt: null,
  };
  return state;
};

export default new Vuex.Store<RootState>({
  state: {
    ...rootState(),
  },
  actions: {
    [ActionsKeys.SET_TOS_ACCEPTED]: async ({ commit }) => {
      await API.post(
        'https://api.dev.otis-platform.tech/api/v0/auth/user/self/tnc_accept/',
        {},
        { baseURL: '', headers: { Authorization: `Bearer ${await AzureAuth.getToken()}` } }
      );
      commit(MutationsKeys.SET_TOS_ACCEPTED);
    },
    [ActionsKeys.SET_CURRENT_USER]: ({ commit, dispatch }, payload: ActionsPayload['SET_CURRENT_USER']) => {
      commit(MutationsKeys.SET_CURRENT_USER, payload);
      router.push('/');
      dispatch(SocketActionsKeys.CONNECT_SOCKET);
    },
    [ActionsKeys.CHECK_LOGGED_IN]: async ({ commit, getters }) => {
      try {
        API.defaults.headers.common['Authorization'] = `Bearer ${getters.jwt}`;
        await API.post('/auth/login_check');
      } catch {
        commit(MutationsKeys.CLEAR_CURRENT_USER);
        router.push('/auth');
      }
    },
    [ActionsKeys.OPEN_DASHBOARD]: ({ dispatch }) => {
      dispatch(BoardActionsKeys.CLOSE_BOARD);
      dispatch(SessionActionsKeys.LEAVE_SESSION);
    },
    [ActionsKeys.LOGOUT]: async ({ commit, dispatch }, payload) => {
      dispatch(SessionActionsKeys.LEAVE_SESSION);
      dispatch(SocketActionsKeys.DISCONNECT_SOCKET);
      dispatch(SocketActionsKeys.RESET_STATE);
      commit(MutationsKeys.RESET_STATE);
      if (!payload) await router.push('/auth');
    },
    [ActionsKeys.ROLE_CHANGING]: async ({ commit, dispatch }) => {
      await dispatch(SessionActionsKeys.LEAVE_SESSION);
      await dispatch(SocketActionsKeys.DISCONNECT_SOCKET);
      await dispatch(SocketActionsKeys.RESET_STATE);
      await commit(MutationsKeys.CLEAR_CURRENT_USER);
      await router.push('/auth');
    },
  },
  mutations: {
    [MutationsKeys.SET_TOS_ACCEPTED]: (state, payload = true) => {
      state.tnc_accepted = payload;
    },
    [MutationsKeys.SET_CURRENT_USER]: (state, payload: MutationsPayload['SET_CURRENT_USER']) => {
      state.currentUser = payload.user;
      state.jwt = payload.jwt;
    },
    [MutationsKeys.CLEAR_CURRENT_USER]: state => {
      state.currentUser = null;
      state.jwt = null;
    },
    [MutationsKeys.RESET_STATE]: state => {
      Object.assign(state, rootState());
    },
  },
  getters: {
    [GettersKeys.tosAccepted]: state => state.tnc_accepted,
    [GettersKeys.isLoggedIn]: state => !!state.currentUser,
    [GettersKeys.currentUser]: state => state.currentUser,
    [GettersKeys.isGuest]: state => state.currentUser?.type === 'Guest',
    [GettersKeys.jwt]: state => state.jwt,
  },
  modules: {
    Sockets,
    Interface,
    Board,
  },
  plugins: [
    createPersistedState({
      reducer: (state: RootState) => {
        if (state.currentUser) {
          return state;
        } else {
          return {};
        }
      },
    }),
  ],
});
